/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import { HomePage } from './pages/HomePage/Loadable';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import { useTranslation } from 'react-i18next';
import { LoginPage } from './pages/LoginPage';
import { UserPage } from './pages/UserPage/Loadable';
import { BannerPage } from './pages/BannerPage/Loadable';
import { ModalPage } from './pages/ModalPage/Loadable';
import { CategoryPage } from './pages/CategoryPage/Loadable';
import { PresentPage } from './pages/PresentPage/Loadable';
import { PresentAddPage } from './pages/PresentPage/PresentAddPage/Loadable';
import { PresentRequestPage } from './pages/PresentRequestPage/Loadable';
import { PointRulePage } from './pages/PointRulePage/Loadable';
import { EventPage } from './pages/EventPage';
import { DailyReportPage } from './pages/DailyReportPage/Loadable';
import { UserPointPage } from './pages/UserPointPage/Loadable';
import { UserPointHistoryPage } from './pages/UserPointHistoryPage/Loadable';
import { InvitationHistoryPage } from './pages/InvitationHistoryPage/Loadable';
import { SurveyPage } from './pages/SurveyPage';
import { ExportPage } from './pages/ExportPage/Loadable';
import { EventAddPage } from './pages/EventPage/EventAddPage/Loadable';
import { SurveyAddPage } from './pages/SurveyPage/SurveyAddPage';
import { useCommonSlice } from '../store/commonSlice';
import { SurveyEditPage } from './pages/SurveyPage/SurveyEditPage';
import { PresentEditPage } from './pages/PresentPage/PresentEditPage/Loadable';
import PrivateRoute from '../utils/auth/PrivateRoute';
import { LogoutPage } from './pages/LogoutPage';
import { EventEditPage } from './pages/EventPage/EventEditPage';
import { TestPage } from './pages/TestPage';
import { ForceUpdateMaintenance } from './pages/ForceUpdateMaintenancePage';
import { RedirectPage } from './pages/RedirectPage';
import { WallReportPage } from './pages/WallReportPage';
import { ModifyTelInputCountPage } from './pages/SmsAuthPage';
import { OfferwallPage } from './pages/OfferwallPage';
import { OfferwallModifyPage } from './pages/OfferwallModifyPage';
import { OfferwallCsvPage } from './pages/OfferwallCsvPage';
import { StatisticsPage } from './pages/StatisticsPage';
import { ImportPage } from './pages/ImportPage';
import { ImportHistoryPage } from './pages/ImportHistoryPage';
import { PointUsedLogPage } from './pages/PointUsedLogPage/Loadable';
import { KyashConfigPage } from './pages/KyashConfigPage/Loadable';
import { NotificationPage } from './pages/NotificationPage/Loadable';
import { TermsPage } from './pages/TermsPage/Loadable';
import {
  ADMIN_PERMISSION,
  clearAdminSession,
  getDefaultAdminRoute,
  hasValidAdminSession,
  hasAdminPermission,
} from '../utils/auth/adminPermissions';


export function App() {
  const { i18n } = useTranslation();
  const { actions } = useCommonSlice();
  const hasAdminSession = hasValidAdminSession();

  if (!hasAdminSession) {
    clearAdminSession();
  }

  return (
    <BrowserRouter>
      <Helmet
        titleTemplate="%s - Voice 管理ツール"
        defaultTitle="Voice 管理ツール"
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta name="description" content="Voice 管理ツール" />
        <meta name="robots" content='noindex,nofollow,noarchive' />
      </Helmet>
      <Switch>
        <Route exact path="/login" component={LoginPage} />

        <PrivateRoute
          path={'/banner'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <BannerPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/top-view'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <HomePage />
        </PrivateRoute>

        <PrivateRoute
          path={'/users'}
          permission={ADMIN_PERMISSION.ADMIN_USER_MANAGE}
        >
          <UserPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/modal'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <ModalPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/category'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <CategoryPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/present/add'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <PresentAddPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/present/edit/:present_id'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <PresentEditPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/present'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <PresentPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/present-request'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <PresentRequestPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/point-used-log'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <PointUsedLogPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/kyash-config'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <KyashConfigPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/notifications'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <NotificationPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/terms'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <TermsPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/point-rule'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <PointRulePage />
        </PrivateRoute>

        <PrivateRoute
          path={'/invitation-history'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <InvitationHistoryPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/event/edit/:event_id'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <EventEditPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/event/add'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <EventAddPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/event'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <EventPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/daily_summaries'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <DailyReportPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/user-point-history/:member_type/:id'}
          permission={ADMIN_PERMISSION.USER_POINT_HISTORY_VIEW}
        >
          <UserPointHistoryPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/user-point'}
          permission={ADMIN_PERMISSION.USER_POINT_VIEW}
        >
          <UserPointPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/survey/add'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <SurveyAddPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/survey/edit/:survey_id'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <SurveyEditPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/survey'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <SurveyPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/export'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <ExportPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/ForceUpdateMaintenance'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <ForceUpdateMaintenance />
        </PrivateRoute>

        <PrivateRoute
          path={'/SmsAuthPage'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <ModifyTelInputCountPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/OfferwallPage'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <OfferwallPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/OfferwallCsvPage'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <OfferwallCsvPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/OfferwallModifyPage'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <OfferwallModifyPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/WallReportPage'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <WallReportPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/StatisticsPage'}
          permission={ADMIN_PERMISSION.GENERAL_ADMIN}
        >
          <StatisticsPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/ImportPage'}
          permission={ADMIN_PERMISSION.IMPORT}
        >
          <ImportPage />
        </PrivateRoute>

        <PrivateRoute
          path={'/ImportHistoryPage'}
          permission={ADMIN_PERMISSION.IMPORT}
        >
          <ImportHistoryPage />
        </PrivateRoute>

        <PrivateRoute path={'/test'}>
          <TestPage />
        </PrivateRoute>

        <PrivateRoute path={'/logout'}>
          <LogoutPage />
        </PrivateRoute>

        <Route exact={true} path={'/'}>
          {hasAdminSession ? (
            hasAdminPermission(ADMIN_PERMISSION.GENERAL_ADMIN) ? (
              <HomePage />
            ) : (
              <Redirect to={getDefaultAdminRoute()} />
            )
          ) : (
            <RedirectPage />
          )}
        </Route>

        <Route exact={true} path={'*'} component={NotFoundPage} />
      </Switch>
    </BrowserRouter>
  );
}
